import React from 'react';
import { Box, Paper, Typography, Tabs, Tab, Grid, Button, TextField, MenuItem, Alert, CircularProgress, Divider, Chip, IconButton, Tooltip, List, ListItem, ListItemText, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import goldColors from '../themes/goldColors';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMonthlyReport, fetchYearlyReport, fetchCategoryReport } from '../slices/reportSlice';
import { fetchTransactions } from '../slices/transactionSlice';
// 8 sep 2026 (pedido de Augusto: "los informes aparecen en dolares,
// deberia dar la opcion de dolares o soles, pero siempre mostrarlo en la
// moneda de donde es el usuario" -- ojo: el default NO es soles fijo,
// sino la moneda del propio usuario, ver detectDefaultCurrency mas
// abajo): formatCurrency de formatHelpers.js estaba hardcodeado a USD --
// ese era el bug. Se reemplaza por el formatCurrency real de
// currencyUtils.js (mismo que usa CurrencyContext/DebtsPage), que si
// formatea segun el codigo de moneda que se le pase.
import { formatCurrency, detectDefaultCurrency } from '../utils/currencyUtils';
import { useLanguage } from '../contexts/LanguageContext';
import reportService from '../services/reportService';
import apiClient from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { esAdmin } from '../utils/roles';
// 7 sep 2026 (pedido de Augusto: "mejorar los reportes básicos... para
// el usuario básico no el premium"): este componente ya existía y ya
// funciona en el Panel (NotionDashboard.js) desde el 28 ago 2026 -- lee
// GET /reports/trend (sin IA, disponible para Personal) y muestra la
// tendencia de ahorro/deuda de los últimos meses. Nunca se agregó acá,
// en Informes, así que hasta hoy nadie lo veía desde esta pantalla.
import SavingsDebtTrend from './SavingsDebtTrend';
// 17 sep 2026 (pedido de Augusto: "el gráfico tiene que tener colores que
// diferencien, esos matices del dorado no ayudan"): antes esta paleta
// eran puros matices de dorado (GOLD_COLORS_PIE) -- se veían casi
// idénticos entre sí en una torta con varias categorías, justo lo
// opuesto de lo que hace falta para distinguir una porción de otra de un
// vistazo. Paleta categórica de 12 colores bien diferenciados entre sí
// (mismo criterio que paletas tipo Tableau/D3 category10, pensada para
// que colores vecinos en la lista no se confundan). El dorado de la
// marca (goldColors.main) sigue usándose en barras/líneas -- ver
// SpendingChart.js -- solo se sacó de ESTE gráfico de torta.
const CATEGORY_COLORS_PIE = [
  '#4E79A7',
  '#F28E2B',
  '#E15759',
  '#76B7B2',
  '#59A14F',
  '#EDC948',
  '#B07AA1',
  '#FF9DA7',
  '#9C755F',
  '#BAB0AC',
  '#86BCB6',
  '#D37295',
];

const MESES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const MESES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const Reports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const months = isSpanish ? MESES_ES : MESES_EN;
  // 28 ago 2026 (Augusto: "necesito el mismo sistema utilizado para
  // importar documentos para mí como administrador"): mismo mecanismo de
  // "Ignorar y continuar" que StatementImporter.js -- el control de
  // acceso real vive en el backend (ver admin_override en
  // reports.py::ask_report), esto es solo para no confundir al resto de
  // clientes con un botón que igual el backend les va a rechazar.
  // 17 sep 2026: la lista de emails vive ahora en utils/roles.js.
  const { user } = useAuth();
  const isAdminUser = esAdmin(user);
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth() + 1);
  const [selectedCategory, setSelectedCategory] = React.useState('');

  // 8 sep 2026 (ver comentario del import de detectDefaultCurrency
  // arriba): moneda en la que se muestran TODOS los informes de esta
  // pantalla (excepto Presupuesto, que por diseno siempre queda en
  // soles -- ver comentario junto a budgetChartData mas abajo). Arranca
  // en la moneda del propio usuario (User.country -> detectDefaultCurrency,
  // mismo mecanismo que CurrencyContext/DebtsPage), restringida a
  // PEN/USD porque el backend (exchange_rate.py) solo sabe convertir
  // entre esas dos -- si el pais del usuario resuelve a otra moneda
  // (ej. EUR), cae a USD.
  const [moneda, setMoneda] = React.useState(() => {
    const detectada = detectDefaultCurrency(user?.country);
    return detectada === 'PEN' ? 'PEN' : 'USD';
  });
  // Tasa manual (edicion tipo "lapiz", mismo patron que DebtsPage.js)
  // para sobreescribir la tasa en vivo que trae el backend -- se manda
  // como tasa_manual a cada endpoint de /reports/*, ver
  // reports.py::_resolver_conversion. Se persiste en localStorage para
  // no perderla al recargar la pagina.
  const TASA_MANUAL_KEY = 'pcm_reports_tasa_manual';
  const [tasaManual, setTasaManualState] = React.useState(() => {
    const guardada = window.localStorage.getItem(TASA_MANUAL_KEY);
    const valor = guardada ? parseFloat(guardada) : NaN;
    return valor > 0 ? valor : null;
  });
  const [editandoTasa, setEditandoTasa] = React.useState(false);
  const [tasaDraft, setTasaDraft] = React.useState('');
  // tipoCambio se llena con la respuesta de cualquier reporte (todos
  // traen el mismo tipo_cambio del dia, ver _resolver_conversion) --
  // debtReport se carga solo al entrar a la pantalla, asi que es la
  // fuente mas confiable para mostrar la fecha/fuente sin depender de
  // que el usuario haya generado un reporte especifico.
  const [tipoCambio, setTipoCambio] = React.useState(null);
  const guardarTasaManual = (valor) => {
    setTasaManualState(valor);
    if (valor && valor > 0) {
      window.localStorage.setItem(TASA_MANUAL_KEY, String(valor));
    } else {
      window.localStorage.removeItem(TASA_MANUAL_KEY);
    }
  };
  const handleStartEditTasa = () => {
    setTasaDraft(tasaManual != null ? String(tasaManual) : (tipoCambio ? String(tipoCambio.promedio) : ''));
    setEditandoTasa(true);
  };
  const handleSaveTasa = () => {
    const valor = parseFloat(tasaDraft);
    if (valor > 0) guardarTasaManual(valor);
    setEditandoTasa(false);
  };
  const handleResetTasa = () => {
    guardarTasaManual(null);
    setEditandoTasa(false);
  };

  // 3 sep 2026 (pedido de Augusto: "enfoquémonos en los reportes
  // básicos"): los 3 reportes de arriba (mensual/anual/categoría) SÍ se
  // calculaban en Redux desde antes, pero el resultado nunca se pintaba
  // en pantalla -- quedaba un "Report Results" vacío. reports viene de
  // reportSlice (state.reports.monthly/yearly/category/loading/error).
  const reports = useSelector((state) => state.reports);

  // Categorías reales del usuario (para el selector de "Reporte por
  // categoría") -- antes esta pantalla tenía una lista fija en inglés
  // ("Food & Dining", "Transport"...) que no correspondía a ninguna
  // categoría real de la app (las categorías son texto libre en
  // español, ver CATEGORIAS en TransactionForm.js y CategoriesPage.js),
  // así que ese reporte nunca encontraba nada. Se derivan de las
  // transacciones ya cargadas, mismo criterio que categoriasEnUso en
  // CategoriesPage.js -- "Transferencia" se excluye porque no es una
  // categoría de gasto/ingreso real (ver CATEGORIA_TRANSFERENCIA en el
  // backend).
  const transactionsList = useSelector((state) => state.transactions.list);
  React.useEffect(() => {
    if (!transactionsList || transactionsList.length === 0) {
      dispatch(fetchTransactions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const categoriasReales = React.useMemo(() => {
    const set = new Set();
    (transactionsList || []).forEach((t) => {
      if (t.categoria && t.categoria !== 'Transferencia') set.add(t.categoria);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [transactionsList]);

  // 3 sep 2026: los otros 3 reportes básicos que el backend ya calculaba
  // (spending-pattern, budget, debt) pero que esta pantalla nunca
  // llamaba -- se cargan solos al entrar (son "estado actual", no piden
  // año/mes) con un botón de refrescar por si cambiaron datos mientras
  // la pantalla está abierta.
  const [spendingPattern, setSpendingPattern] = React.useState(null);
  const [spLoading, setSpLoading] = React.useState(false);
  const [spError, setSpError] = React.useState(null);
  // 8 sep 2026 (Augusto: "no he gastado S/. 37,696 en educacion en
  // Setiembre 2026, por que me sale esto en informes" -- el backend
  // sumaba TODO el historial sin filtrar fecha; "todo reporte tiene que
  // ir de acuerdo a los filtros"): ahora manda selectedYear/selectedMonth
  // (el mismo selector de arriba) igual que el Reporte mensual.
  const cargarSpendingPattern = React.useCallback(async () => {
    setSpLoading(true);
    setSpError(null);
    try {
      const { data } = await reportService.getSpendingPattern(selectedYear, selectedMonth, moneda, tasaManual);
      setSpendingPattern(data);
    } catch (err) {
      setSpError(isSpanish ? 'No se pudo cargar el gasto por categoría.' : 'Could not load spending by category.');
    } finally {
      setSpLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpanish, moneda, tasaManual, selectedYear, selectedMonth]);

  const [budgetReport, setBudgetReport] = React.useState(null);
  const [budgetLoading, setBudgetLoading] = React.useState(false);
  const [budgetError, setBudgetError] = React.useState(null);
  const cargarBudgetReport = React.useCallback(async () => {
    setBudgetLoading(true);
    setBudgetError(null);
    try {
      const { data } = await reportService.getBudgetReport();
      setBudgetReport(data);
    } catch (err) {
      setBudgetError(isSpanish ? 'No se pudo cargar el presupuesto.' : 'Could not load the budget.');
    } finally {
      setBudgetLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpanish]);

  const [debtReport, setDebtReport] = React.useState(null);
  const [debtLoading, setDebtLoading] = React.useState(false);
  const [debtError, setDebtError] = React.useState(null);
  const cargarDebtReport = React.useCallback(async () => {
    setDebtLoading(true);
    setDebtError(null);
    try {
      const { data } = await reportService.getDebtReport(moneda, tasaManual);
      setDebtReport(data);
      if (data && data.tipo_cambio) setTipoCambio(data.tipo_cambio);
    } catch (err) {
      setDebtError(isSpanish ? 'No se pudo cargar la deuda.' : 'Could not load debt.');
    } finally {
      setDebtLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpanish, moneda, tasaManual]);

  React.useEffect(() => {
    cargarSpendingPattern();
    cargarBudgetReport();
    cargarDebtReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 11 ago 2026 (pedido de Augusto): reporte narrativo real con IA, ver
  // reportService.getAiSummary -> backend_new/.../reports.py::ai_summary_report.
  // Estado propio (no Redux) porque es autocontenido a esta tarjeta y no lo
  // necesita ninguna otra pantalla, igual que el patrón usado en
  // CuentasPage.js/DebtsPage.js para el refresco de onboarding.
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState(null);
  const [aiResult, setAiResult] = React.useState(null);
  const handleGenerateAiSummary = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const { data } = await reportService.getAiSummary(selectedYear, selectedMonth, moneda, tasaManual);
      setAiResult(data);
      if (data && data.tipo_cambio) setTipoCambio(data.tipo_cambio);
    } catch (err) {
      setAiError(
        err?.response?.data?.detail ||
          (language === 'es'
            ? 'No se pudo generar el reporte con IA. Intenta de nuevo en unos segundos.'
            : 'Could not generate the AI report. Try again in a few seconds.')
      );
    } finally {
      setAiLoading(false);
    }
  };
  // 28 ago 2026 (pedido de Augusto: "podemos utilizar la opción de AI para
  // hacer preguntas sobre lo que se tiene y hacer estimados?"): a
  // diferencia del "Reporte Inteligente (IA)" de arriba, que siempre
  // genera el mismo tipo de resumen fijo para un mes puntual, esto deja
  // escribir cualquier pregunta libre -- ver backend
  // endpoints/reports.py::ask_report (POST /reports/ask), que arma un
  // contexto de los últimos 12 meses + categorías + presupuestos + deudas
  // y se lo manda a Claude junto con la pregunta. El historial de
  // preguntas es solo de esta sesión de pantalla (no se persiste): cada
  // pregunta nueva se agrega arriba de la anterior para no tener que subir
  // el scroll a leer la respuesta más reciente.
  const [pregunta, setPregunta] = React.useState('');
  const [askLoading, setAskLoading] = React.useState(false);
  const [askError, setAskError] = React.useState(null);
  // 28 ago 2026 (misma cuota compartida que statements.py -- ver
  // check_page_quota en plans.py): cuando /reports/ask responde 402 es
  // porque se acabaron las páginas del mes (plan + saldo extra), igual que
  // al importar un estado de cuenta -- así que mostramos el mismo botón
  // "Comprar páginas" que StatementImporter.js, en vez de solo el texto
  // del error.
  const [askQuotaExceeded, setAskQuotaExceeded] = React.useState(false);
  const [historialPreguntas, setHistorialPreguntas] = React.useState([]);
  // adminOverride=true solo cuando isAdminUser hace click en "Ignorar y
  // continuar" tras un 402 (ver botón junto al Alert de askError) -- el
  // backend vuelve a validar el email antes de saltarse la cuota (ver
  // admin_override en ask_report), así que esto es solo la señal desde el
  // frontend, no la única barrera.
  const handleAskAi = async (adminOverride = false) => {
    const p = pregunta.trim();
    if (!p) return;
    setAskLoading(true);
    setAskError(null);
    setAskQuotaExceeded(false);
    try {
      const { data } = await apiClient.post('/reports/ask', {
        pregunta: p,
        admin_override: adminOverride,
        moneda,
        ...(tasaManual ? { tasa_manual: tasaManual } : {}),
      });
      // 28 ago 2026 (pedido de Augusto: "debe de poder hacerme gráficos
      // como se le pide"): data.grafico viene null cuando la pregunta no
      // amerita comparar varias cifras -- ver ASK_SYSTEM_PROMPT en el
      // backend.
      setHistorialPreguntas((prev) => [
        { pregunta: data.pregunta, respuesta: data.respuesta, grafico: data.grafico },
        ...prev,
      ]);
      if (data && data.tipo_cambio) setTipoCambio(data.tipo_cambio);
      setPregunta('');
    } catch (err) {
      setAskQuotaExceeded(err?.response?.status === 402);
      setAskError(
        err?.response?.data?.detail ||
          (language === 'es'
            ? 'No se pudo obtener respuesta. Intenta de nuevo en unos segundos.'
            : 'Could not get an answer. Try again in a few seconds.')
      );
    } finally {
      setAskLoading(false);
    }
  };
  const handleAskKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAskAi();
    }
  };
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };
  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };
  const handleGenerateMonthlyReport = () => {
    dispatch(fetchMonthlyReport({ year: selectedYear, month: selectedMonth, moneda, tasaManual }));
  };
  const handleGenerateYearlyReport = () => {
    dispatch(fetchYearlyReport({ year: selectedYear, moneda, tasaManual }));
  };
  const handleGenerateCategoryReport = () => {
    if (!selectedCategory) return;
    dispatch(fetchCategoryReport({ category: selectedCategory, year: selectedYear, month: selectedMonth, moneda, tasaManual }));
  };

  // 8 sep 2026 (ver comentario junto al estado "moneda" arriba): cuando
  // el usuario cambia de moneda (o edita la tasa manual), se refrescan
  // los informes que no dependen de un filtro propio (gasto por
  // categoria, deuda) y se recalculan los que el usuario ya habia
  // generado -- para que "cambiar a dolares" se sienta inmediato en vez
  // de tener que volver a apretar "Generar reporte". Presupuesto queda
  // afuera a proposito (ver comentario junto a budgetChartData mas
  // abajo): nunca se recalcula con la moneda elegida aqui. El ref evita
  // que el primer render (que ya carga todo en el useEffect de montaje
  // de arriba) dispare una segunda carga duplicada.
  const esPrimeraCargaMoneda = React.useRef(true);
  React.useEffect(() => {
    if (esPrimeraCargaMoneda.current) {
      esPrimeraCargaMoneda.current = false;
      return;
    }
    cargarSpendingPattern();
    cargarDebtReport();
    if (reports.monthly) {
      dispatch(fetchMonthlyReport({ year: selectedYear, month: selectedMonth, moneda, tasaManual }));
    }
    if (reports.yearly) {
      dispatch(fetchYearlyReport({ year: selectedYear, moneda, tasaManual }));
    }
    if (reports.category && selectedCategory) {
      dispatch(fetchCategoryReport({ category: selectedCategory, year: selectedYear, month: selectedMonth, moneda, tasaManual }));
    }
    if (aiResult) {
      handleGenerateAiSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moneda, tasaManual]);

  // 8 sep 2026 (ver comentario junto a cargarSpendingPattern arriba):
  // "Gasto por categoria" no tiene boton de "Generar reporte" propio --
  // se auto-carga -- asi que cuando el usuario cambia de año/mes arriba,
  // tiene que refrescarse solo para reflejar el periodo elegido, en vez
  // de quedarse con el acumulado de otro mes. El ref evita duplicar la
  // carga del useEffect de montaje.
  const esPrimeraCargaPeriodo = React.useRef(true);
  React.useEffect(() => {
    if (esPrimeraCargaPeriodo.current) {
      esPrimeraCargaPeriodo.current = false;
      return;
    }
    cargarSpendingPattern();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  // Datos ya en forma de array para los gráficos de barras
  // ingresos/gastos de Reporte Mensual/Anual.
  const monthlyChartData = reports.monthly
    ? [
        { nombre: isSpanish ? 'Ingresos' : 'Income', valor: reports.monthly.ingresos },
        { nombre: isSpanish ? 'Gastos' : 'Expenses', valor: reports.monthly.gastos },
      ]
    : [];
  const yearlyChartData = reports.yearly
    ? [
        { nombre: isSpanish ? 'Ingresos' : 'Income', valor: reports.yearly.ingresos },
        { nombre: isSpanish ? 'Gastos' : 'Expenses', valor: reports.yearly.gastos },
      ]
    : [];
  // 7 sep 2026 (pedido de Augusto: comparativas mes a mes para el usuario
  // básico): antes de esto, la comparación contra el mes anterior solo la
  // veía el usuario Pro+, enterrada en el párrafo del "Reporte
  // inteligente (IA)" -- acá se muestra como un indicador simple
  // ("+12% vs. mes pasado") junto al número absoluto. esMasEsMejor
  // decide el color: en Ingresos, más es mejor (verde); en Gastos, más es
  // peor (rojo) -- convención estándar, no juzga el caso puntual del
  // usuario.
  const renderVariacionMensual = (actual, anterior, esMasEsMejor) => {
    if (anterior === null || anterior === undefined) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {isSpanish ? 'Sin mes anterior para comparar' : 'No previous month to compare'}
        </Typography>
      );
    }
    const diferencia = actual - anterior;
    // anterior === 0 -- no se puede calcular un porcentaje (división por
    // cero), se muestra el monto de diferencia en vez de un % inventado.
    if (anterior === 0) {
      if (diferencia === 0) return null;
      const subio = diferencia > 0;
      const bueno = subio === esMasEsMejor;
      return (
        <Typography variant="caption" sx={{ display: 'block', color: bueno ? 'success.main' : 'error.main', fontWeight: 600 }}>
          {(subio ? '+' : '') + fmt(diferencia)} {isSpanish ? `vs. mes pasado (${fmt(0)})` : `vs. last month (${fmt(0)})`}
        </Typography>
      );
    }
    const pct = (diferencia / anterior) * 100;
    const subio = pct > 0;
    const bueno = subio === esMasEsMejor;
    const pctTexto = `${subio ? '+' : ''}${pct.toFixed(0)}%`;
    return (
      <Typography variant="caption" sx={{ display: 'block', color: Math.abs(pct) < 1 ? 'text.secondary' : bueno ? 'success.main' : 'error.main', fontWeight: 600 }}>
        {Math.abs(pct) < 1
          ? (isSpanish ? 'Igual que el mes pasado' : 'Same as last month')
          : `${pctTexto} ${isSpanish ? 'vs. mes pasado' : 'vs. last month'}`}
      </Typography>
    );
  };
  const spendingChartData = spendingPattern
    ? Object.entries(spendingPattern)
        .map(([categoria, total]) => ({ categoria, total }))
        .sort((a, b) => b.total - a.total)
    : [];

  // 7 sep 2026 (pedido de Augusto: alertas visuales de presupuesto para
  // el usuario básico): antes "Presupuesto vs. gastado" era un gráfico
  // de barras neutro -- dos barras del mismo color para toda categoría,
  // sin importar si ya se pasó del límite o recién va por la mitad. Se
  // calcula el % usado por categoría y se clasifica en 3 niveles (mismo
  // criterio que un semáforo, estándar en apps de presupuesto): verde
  // <80%, ámbar 80-99%, rojo >=100% (ya se pasó). presupuesto=0 se trata
  // como "sin límite real" (no se puede calcular %, no se marca en rojo
  // solo por eso).
  const UMBRAL_AMBAR = 80;
  const colorEstadoPresupuesto = (pct) => {
    if (pct === null) return goldColors.main;
    if (pct >= 100) return '#c62828';
    if (pct >= UMBRAL_AMBAR) return '#f9a825';
    return '#2e7d32';
  };
  const budgetChartData = (budgetReport || []).map((b) => {
    const pct = b.presupuesto > 0 ? (b.gastado / b.presupuesto) * 100 : null;
    return { ...b, pct, colorGastado: colorEstadoPresupuesto(pct) };
  });
  const categoriasEnRojo = budgetChartData.filter((b) => b.pct !== null && b.pct >= 100);
  const categoriasEnAmbar = budgetChartData.filter((b) => b.pct !== null && b.pct >= UMBRAL_AMBAR && b.pct < 100);

  // 8 sep 2026 (ver comentario junto al estado "moneda" arriba): fmt
  // formatea en la moneda elegida por el usuario (default: la suya
  // propia, nunca soles fijo); fmtPEN queda hardcodeado a soles SOLO
  // para Presupuesto vs. gastado, que por diseno nunca se convierte
  // (ver comentario de budgetChartData mas arriba -- presupuesto solo en
  // soles por ahora, igual que el resto de la app nunca mezcla soles y
  // dolares en un mismo total).
  const fmt = (amount) => formatCurrency(amount, moneda);
  const fmtPEN = (amount) => formatCurrency(amount, 'PEN');
  // 17 sep 2026: hace falta también en USD -- ver comentario junto al
  // render de debtReport.deudas más abajo (cada tarjeta agrupada puede
  // tener saldo en soles Y en dólares a la vez, y ahora se muestran los
  // dos en vez de convertir todo a una sola moneda).
  const fmtUSD = (amount) => formatCurrency(amount, 'USD');

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Informes' : 'Reports'}
      </Typography>

      {/* 8 sep 2026: ver comentario junto al estado "moneda" arriba --
          todos los informes de esta pantalla (menos Presupuesto) se
          muestran en esta moneda, con la tasa del dia (o la que el
          usuario sobreescriba a mano). Mismo patron de ToggleButtonGroup
          que CalendarPage.js, y de edicion de tasa "lapiz" que
          DebtsPage.js -- ver comentarios junto al estado moneda/tasaManual
          mas arriba. */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <ToggleButtonGroup
          value={moneda}
          exclusive
          size="small"
          onChange={(_e, nueva) => { if (nueva) setMoneda(nueva); }}
        >
          <ToggleButton value="PEN">{isSpanish ? 'Soles' : 'Soles'}</ToggleButton>
          <ToggleButton value="USD">{isSpanish ? 'Dólares' : 'Dollars'}</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {editandoTasa ? (
            <>
              <TextField
                size="small"
                type="number"
                label={isSpanish ? 'Tasa (S/ por US$)' : 'Rate (S/ per US$)'}
                value={tasaDraft}
                onChange={(e) => setTasaDraft(e.target.value)}
                sx={{ width: 170 }}
              />
              <Button size="small" onClick={handleSaveTasa}>{isSpanish ? 'Guardar' : 'Save'}</Button>
              <Button size="small" color="inherit" onClick={() => setEditandoTasa(false)}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
            </>
          ) : (
            <>
              {tipoCambio ? (
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Tipo de cambio del' : 'Exchange rate for'} {tipoCambio.fecha} (
                  {isSpanish ? 'compra' : 'buy'} S/{tipoCambio.compra} {'\u00b7'} {isSpanish ? 'venta' : 'sell'} S/{tipoCambio.venta} {'\u00b7'}{' '}
                  {isSpanish ? 'promedio usado' : 'average used'} S/{tasaManual != null ? tasaManual : tipoCambio.promedio} {isSpanish ? 'por' : 'per'} US$
                  {tasaManual != null ? ' (manual)' : ''}) {'\u00b7'} {isSpanish ? 'Fuente' : 'Source'}: {tipoCambio.fuente}
                </Typography>
              ) : (
                <Typography variant="caption" color="warning.main">
                  {isSpanish
                    ? 'No se pudo obtener el tipo de cambio del día -- los montos en otra moneda pueden no estar convertidos.'
                    : "Could not get today's exchange rate -- amounts in the other currency may not be converted."}
                </Typography>
              )}
              <Tooltip title={isSpanish ? 'Editar tasa manualmente' : 'Edit rate manually'}>
                <IconButton size="small" onClick={handleStartEditTasa}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {tasaManual != null && (
                <Button size="small" color="inherit" onClick={handleResetTasa}>
                  {isSpanish ? 'Usar tasa del día' : "Use today's rate"}
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={isSpanish ? 'Reporte mensual' : 'Monthly Report'} />
          <Tab label={isSpanish ? 'Reporte anual' : 'Yearly Report'} />
          <Tab label={isSpanish ? 'Reporte por categoría' : 'Category Report'} />
        </Tabs>
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label={isSpanish ? 'Año' : 'Year'}
                  value={selectedYear}
                  onChange={handleYearChange}
                  sx={{ mb: 2 }}
                >
                  {[...Array(10)].map((_, index) => (
                    <MenuItem key={index} value={new Date().getFullYear() - index}>
                      {new Date().getFullYear() - index}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label={isSpanish ? 'Mes' : 'Month'}
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  sx={{ mb: 2 }}
                >
                  {months.map((month, index) => (
                    <MenuItem key={month} value={index + 1}>
                      {month}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={handleGenerateMonthlyReport}
                  disabled={reports.loading}
                  sx={{ mt: 2 }}
                >
                  {isSpanish ? 'Generar reporte' : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={isSpanish ? 'Año' : 'Year'}
                  value={selectedYear}
                  onChange={handleYearChange}
                  sx={{ mb: 2 }}
                >
                  {[...Array(10)].map((_, index) => (
                    <MenuItem key={index} value={new Date().getFullYear() - index}>
                      {new Date().getFullYear() - index}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handleGenerateYearlyReport}
                  disabled={reports.loading}
                  sx={{ mt: 2 }}
                >
                  {isSpanish ? 'Generar reporte' : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {activeTab === 2 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={isSpanish ? 'Categoría' : 'Category'}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="" disabled>
                    {categoriasReales.length === 0
                      ? (isSpanish ? 'Todavía no tienes categorías' : "You don't have categories yet")
                      : (isSpanish ? 'Elige una categoría' : 'Choose a category')}
                  </MenuItem>
                  {categoriasReales.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handleGenerateCategoryReport}
                  disabled={reports.loading || !selectedCategory}
                  sx={{ mt: 2 }}
                >
                  {isSpanish ? 'Generar reporte' : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* 3 sep 2026: resultado del tab activo -- antes este espacio
          quedaba vacío ("Report results will be displayed here") aunque
          reports.monthly/yearly/category sí se calculaban en Redux. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isSpanish ? 'Resultado' : 'Report Results'}
        </Typography>
        {reports.loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Calculando...' : 'Calculating...'}
            </Typography>
          </Box>
        )}
        {reports.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {isSpanish ? 'No se pudo generar el reporte.' : 'Could not generate the report.'}
          </Alert>
        )}

        {activeTab === 0 && !reports.loading && (
          reports.monthly ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {months[reports.monthly.month - 1]} {reports.monthly.year}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">{isSpanish ? 'Ingresos' : 'Income'}</Typography>
                  <Typography variant="h6" color="success.main">{fmt(reports.monthly.ingresos)}</Typography>
                  {renderVariacionMensual(reports.monthly.ingresos, reports.monthly.prev_ingresos, true)}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">{isSpanish ? 'Gastos' : 'Expenses'}</Typography>
                  <Typography variant="h6" color="error.main">{fmt(reports.monthly.gastos)}</Typography>
                  {renderVariacionMensual(reports.monthly.gastos, reports.monthly.prev_gastos, false)}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Balance</Typography>
                  <Typography variant="h6" color={reports.monthly.balance >= 0 ? 'success.main' : 'error.main'}>
                    {fmt(reports.monthly.balance)}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nombre" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => fmt(value)} />
                    <Bar dataKey="valor" fill={goldColors.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Elige año y mes, y genera el reporte.' : 'Choose a year and month, then generate the report.'}
            </Typography>
          )
        )}

        {activeTab === 1 && !reports.loading && (
          reports.yearly ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {reports.yearly.year}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">{isSpanish ? 'Ingresos' : 'Income'}</Typography>
                  <Typography variant="h6" color="success.main">{fmt(reports.yearly.ingresos)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">{isSpanish ? 'Gastos' : 'Expenses'}</Typography>
                  <Typography variant="h6" color="error.main">{fmt(reports.yearly.gastos)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Balance</Typography>
                  <Typography variant="h6" color={reports.yearly.balance >= 0 ? 'success.main' : 'error.main'}>
                    {fmt(reports.yearly.balance)}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nombre" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => fmt(value)} />
                    <Bar dataKey="valor" fill={goldColors.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Elige un año y genera el reporte.' : 'Choose a year, then generate the report.'}
            </Typography>
          )
        )}

        {activeTab === 2 && !reports.loading && (
          reports.category ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {months[reports.category.month - 1]} {reports.category.year}
              </Typography>
              <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">{isSpanish ? 'Categoría' : 'Category'}</Typography>
                <Typography variant="h6">{reports.category.categoria}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography variant="h6">{fmt(reports.category.total)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Movimientos' : 'Movements'}
                </Typography>
                <Typography variant="h6">{reports.category.cantidad_movimientos}</Typography>
              </Grid>
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Elige una categoría y genera el reporte.' : 'Choose a category, then generate the report.'}
            </Typography>
          )
        )}
      </Paper>

      {/* 3 sep 2026: gasto por categoría (GET /reports/spending-pattern) --
          existía en el backend desde antes pero esta pantalla nunca lo
          llamaba. Se carga solo al entrar, con botón de refrescar. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="h6">
            {isSpanish ? 'Gasto por categoría' : 'Spending by category'}
          </Typography>
          <Tooltip title={isSpanish ? 'Actualizar' : 'Refresh'}>
            <IconButton size="small" onClick={cargarSpendingPattern} disabled={spLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {/* 8 sep 2026 (Augusto: "no he gastado S/. 37,696 en educacion en
            Setiembre 2026, por que me sale esto" -- este card mostraba el
            acumulado de TODA la vida de la cuenta sin decir de que
            periodo era, aunque arriba hubiera un selector de año/mes en
            "Setiembre 2026" que en realidad no lo afectaba): ahora SI lo
            afecta (ver cargarSpendingPattern) y se aclara el periodo aca
            para que no vuelva a pasar la confusion. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {months[selectedMonth - 1]} {selectedYear}
        </Typography>
        {spLoading && <CircularProgress size={20} />}
        {spError && <Alert severity="error">{spError}</Alert>}
        {!spLoading && !spError && spendingChartData.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {isSpanish ? 'Todavía no tienes gastos registrados.' : "You don't have any expenses recorded yet."}
          </Typography>
        )}
        {!spLoading && spendingChartData.length > 0 && (
          <Box sx={{ height: Math.max(260, spendingChartData.length * 20) }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={spendingChartData}
                  dataKey="total"
                  nameKey="categoria"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ categoria, percent }) => `${categoria} (${(percent * 100).toFixed(0)}%)`}
                >
                  {spendingChartData.map((_, i) => (
                    <Cell key={i} fill={CATEGORY_COLORS_PIE[i % CATEGORY_COLORS_PIE.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => fmt(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {/* 7 sep 2026: ver comentario del import de SavingsDebtTrend arriba
          -- componente ya hecho, solo faltaba mostrarlo en Informes. */}
      <SavingsDebtTrend />

      {/* 3 sep 2026: presupuesto vs. gastado (GET /reports/budget) -- mismo
          caso que arriba, ya existía en el backend sin usarse acá. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {isSpanish ? 'Presupuesto vs. gastado' : 'Budget vs. spent'}
          </Typography>
          <Tooltip title={isSpanish ? 'Actualizar' : 'Refresh'}>
            <IconButton size="small" onClick={cargarBudgetReport} disabled={budgetLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {budgetLoading && <CircularProgress size={20} />}
        {budgetError && <Alert severity="error">{budgetError}</Alert>}
        {!budgetLoading && !budgetError && budgetReport && budgetReport.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'Todavía no tienes presupuestos configurados -- créalos en Presupuesto.'
              : "You don't have any budgets set up yet -- create them under Budgets."}
          </Typography>
        )}
        {!budgetLoading && budgetReport && budgetReport.length > 0 && (
          <Box>
            {/* 7 sep 2026: resumen de alertas -- ver comentario junto a
                budgetChartData más arriba. Solo se muestra si hay algo que
                avisar, para no ensuciar la pantalla cuando todo está bien. */}
            {(categoriasEnRojo.length > 0 || categoriasEnAmbar.length > 0) && (
              <Alert severity={categoriasEnRojo.length > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {isSpanish ? (
                  <>
                    {categoriasEnRojo.length > 0 &&
                      `${categoriasEnRojo.length} categoría${categoriasEnRojo.length > 1 ? 's ya se pasaron' : ' ya se pasó'} del presupuesto (${categoriasEnRojo.map((c) => c.categoria).join(', ')}).`}
                    {categoriasEnRojo.length > 0 && categoriasEnAmbar.length > 0 && ' '}
                    {categoriasEnAmbar.length > 0 &&
                      `${categoriasEnAmbar.length} categoría${categoriasEnAmbar.length > 1 ? 's están' : ' está'} cerca del límite (${categoriasEnAmbar.map((c) => c.categoria).join(', ')}).`}
                  </>
                ) : (
                  <>
                    {categoriasEnRojo.length > 0 &&
                      `${categoriasEnRojo.length} categor${categoriasEnRojo.length > 1 ? 'ies are' : 'y is'} over budget (${categoriasEnRojo.map((c) => c.categoria).join(', ')}).`}
                    {categoriasEnRojo.length > 0 && categoriasEnAmbar.length > 0 && ' '}
                    {categoriasEnAmbar.length > 0 &&
                      `${categoriasEnAmbar.length} categor${categoriasEnAmbar.length > 1 ? 'ies are' : 'y is'} close to the limit (${categoriasEnAmbar.map((c) => c.categoria).join(', ')}).`}
                  </>
                )}
              </Alert>
            )}
            <Box sx={{ height: Math.max(260, budgetChartData.length * 50) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetChartData} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="categoria" type="category" width={120} fontSize={12} />
                  <RechartsTooltip
                    formatter={(value, name, item) =>
                      name === (isSpanish ? 'Gastado' : 'Spent') && item.payload.pct !== null
                        ? [`${fmtPEN(value)} (${item.payload.pct.toFixed(0)}%)`, name]
                        : [fmtPEN(value), name]
                    }
                  />
                  <Legend />
                  <Bar dataKey="presupuesto" name={isSpanish ? 'Presupuesto' : 'Budget'} fill={goldColors.main} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="gastado" name={isSpanish ? 'Gastado' : 'Spent'} radius={[0, 4, 4, 0]}>
                    {budgetChartData.map((b, idx) => (
                      <Cell key={idx} fill={b.colorGastado} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {isSpanish
                ? 'Verde: menos del 80% usado. Ámbar: 80-99%. Rojo: ya se pasó del presupuesto.'
                : 'Green: under 80% used. Amber: 80-99%. Red: already over budget.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 3 sep 2026: resumen de deuda (GET /reports/debt) -- mismo caso. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {isSpanish ? 'Deuda' : 'Debt'}
          </Typography>
          <Tooltip title={isSpanish ? 'Actualizar' : 'Refresh'}>
            <IconButton size="small" onClick={cargarDebtReport} disabled={debtLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {debtLoading && <CircularProgress size={20} />}
        {debtError && <Alert severity="error">{debtError}</Alert>}
        {!debtLoading && !debtError && debtReport && debtReport.deudas.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {isSpanish ? 'No tienes deudas registradas.' : "You don't have any debts recorded."}
          </Typography>
        )}
        {!debtLoading && debtReport && debtReport.deudas.length > 0 && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Deuda total' : 'Total debt'}
                </Typography>
                <Typography variant="h6" color="error.main">{fmt(debtReport.total_deuda)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  {isSpanish ? 'Pago mensual total' : 'Total monthly payment'}
                </Typography>
                <Typography variant="h6">{fmt(debtReport.pago_mensual_total)}</Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 1 }} />
            {/* 17 sep 2026 (pedido de Augusto: "por qué aparecer repetidas
                cuentas o tarjetas... tiene que ser 1 con deuda en Soles y
                Dolares"): el backend (endpoints/reports.py, debt_report)
                ahora agrupa por tarjeta física antes de mandar la lista --
                ver el comentario largo ahí. Cada fila es una tarjeta/deuda
                real, con su saldo en soles y/o dólares mostrados por
                separado (monto_pen/monto_usd) en vez de una fila por
                moneda con todo convertido a una sola. */}
            <List dense>
              {debtReport.deudas.map((d) => (
                <ListItem key={d.clave} disableGutters>
                  <ListItemText primary={d.nombre} secondary={d.tipo} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {d.monto_pen !== null && d.monto_pen !== undefined && (
                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        {fmtPEN(d.monto_pen)}
                      </Typography>
                    )}
                    {d.monto_usd !== null && d.monto_usd !== undefined && (
                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        {fmtUSD(d.monto_usd)}
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* 11 ago 2026: Reporte Inteligente (IA) -- ver comentario junto a
          handleGenerateAiSummary arriba. Usa el mismo selector de
          año/mes de la pestaña "Monthly Report", independiente de qué
          pestaña esté activa. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">
            {language === 'es' ? 'Reporte Inteligente (IA)' : 'AI Smart Report'}
          </Typography>
          <Chip size="small" label="Beta" color="primary" variant="outlined" sx={{ ml: 1 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {language === 'es'
            ? `Genera un resumen narrativo de ${months[selectedMonth - 1]} ${selectedYear} con hallazgos y recomendaciones, escrito por IA a partir de tus datos reales.`
            : `Generate a narrative summary of ${months[selectedMonth - 1]} ${selectedYear} with findings and recommendations, written by AI from your real data.`}
        </Typography>
        <Button
          variant="contained"
          onClick={handleGenerateAiSummary}
          disabled={aiLoading}
          startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
        >
          {aiLoading
            ? (language === 'es' ? 'Generando...' : 'Generating...')
            : (language === 'es' ? 'Generar reporte con IA' : 'Generate AI report')}
        </Button>
        {aiError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {aiError}
          </Alert>
        )}
        {aiResult && !aiResult.tiene_datos && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {aiResult.narrativa}
          </Alert>
        )}
        {aiResult && aiResult.tiene_datos && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  {language === 'es' ? 'Ingresos' : 'Income'}
                </Typography>
                <Typography variant="h6">{fmt(aiResult.numeros.ingresos)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  {language === 'es' ? 'Gastos' : 'Expenses'}
                </Typography>
                <Typography variant="h6">{fmt(aiResult.numeros.gastos)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  {language === 'es' ? 'Balance' : 'Balance'}
                </Typography>
                <Typography variant="h6" color={aiResult.numeros.balance >= 0 ? 'success.main' : 'error.main'}>
                  {fmt(aiResult.numeros.balance)}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {aiResult.narrativa}
            </Typography>
          </Box>
        )}
      </Paper>
      {/* 28 ago 2026: Pregúntale a la IA -- ver comentario junto a
          handleAskAi arriba. Complementa al Reporte Inteligente: en vez de
          un resumen fijo de un mes, deja escribir cualquier pregunta
          (incluyendo estimados/proyecciones) sobre los últimos 12 meses de
          datos reales. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">
            {language === 'es' ? 'Pregúntale a la IA' : 'Ask the AI'}
          </Typography>
          <Chip size="small" label="Beta" color="primary" variant="outlined" sx={{ ml: 1 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {language === 'es'
            ? 'Escribe una pregunta sobre tus finanzas -- puede pedir un estimado o proyección (ej. "¿cuánto gasté en promedio en comida?", "si sigo así, ¿cuánto voy a gastar en diciembre?"). Se calcula con tus datos reales de los últimos 12 meses.'
            : 'Type a question about your finances -- you can ask for an estimate or projection (e.g. "what did I spend on average on food?", "if I keep this pace, how much will I spend in December?"). Calculated from your real data over the last 12 months.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            placeholder={
              language === 'es'
                ? 'Ej. ¿Cuánto gasto en promedio al mes en Transporte?'
                : 'E.g. What do I spend on average per month on Transport?'
            }
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            onKeyDown={handleAskKeyDown}
          />
          <Button
            variant="contained"
            disabled={askLoading || !pregunta.trim()}
            // 4 sep 2026 (Augusto: "Pregúntale a la IA... no se pudo
            // obtener respuesta", sin ninguna petición "ask" en Network ni
            // error en consola): esto era `onClick={handleAskAi}` --
            // React le pasa el propio SyntheticEvent del clic como primer
            // argumento, que es justo `adminOverride` en la firma de
            // handleAskAi(adminOverride = false). Cada clic normal mandaba
            // el evento del navegador (con referencias circulares) como
            // "admin_override" al armar el JSON del POST -- JSON.stringify
            // truena ANTES de que salga cualquier petición de red, y el
            // catch de handleAskAi absorbe ese error en silencio (por eso
            // no había nada en Network ni en Console). Envolver en una
            // arrow function hace que handleAskAi() se llame SIN
            // argumentos, igual que ya hacía handleAskKeyDown (Enter) --
            // por eso preguntar con Enter en vez de clic sí funcionaba.
            onClick={() => handleAskAi()}
            startIcon={askLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          >
            {askLoading
              ? (language === 'es' ? 'Preguntando...' : 'Asking...')
              : (language === 'es' ? 'Preguntar' : 'Ask')}
          </Button>
        </Box>
        {askError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              askQuotaExceeded ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" size="small" onClick={() => navigate('/subscription')}>
                    {language === 'es' ? 'Comprar páginas' : 'Buy pages'}
                  </Button>
                  {/* 28 ago 2026: solo admins -- ver isAdminUser. Mismo
                      criterio que StatementImporter.js: el backend vuelve
                      a validar el email, ocultar el botón acá es solo
                      cosmético. */}
                  {isAdminUser && (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={askLoading}
                      onClick={() => handleAskAi(true)}
                    >
                      {language === 'es' ? 'Ignorar y continuar' : 'Ignore and continue'}
                    </Button>
                  )}
                </Box>
              ) : undefined
            }
          >
            {askError}
          </Alert>
        )}
        {historialPreguntas.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {historialPreguntas.map((item, idx) => (
              <Box key={idx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {item.pregunta}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                  {item.respuesta}
                </Typography>
                {item.grafico && item.grafico.datos && item.grafico.datos.length > 0 && (
                  <Box sx={{ mt: 2, height: item.grafico.tipo === 'torta' ? 320 : 260 }}>
                    {item.grafico.titulo && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {item.grafico.titulo}
                      </Typography>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                      {/* 28 ago 2026 (pedido de Augusto: "ese no es un
                          gráfico pie" -- antes siempre se dibujaba en
                          barras sin importar el tipo que pidiera):
                          item.grafico.tipo ya viene validado desde el
                          backend (barras/torta/lineas, con "barras" como
                          default si algo raro llega) -- ver parseo en
                          reports.py. */}
                      {item.grafico.tipo === 'torta' ? (
                        <PieChart>
                          <Pie
                            data={item.grafico.datos}
                            dataKey="valor"
                            nameKey="etiqueta"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ etiqueta, percent }) => `${etiqueta} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {item.grafico.datos.map((_, i) => (
                              <Cell key={i} fill={CATEGORY_COLORS_PIE[i % CATEGORY_COLORS_PIE.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => fmt(value)} />
                          <Legend />
                        </PieChart>
                      ) : item.grafico.tipo === 'lineas' ? (
                        <LineChart data={item.grafico.datos} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="etiqueta" angle={-30} textAnchor="end" interval={0} height={60} fontSize={12} />
                          <YAxis fontSize={12} />
                          <RechartsTooltip formatter={(value) => fmt(value)} />
                          <Line type="monotone" dataKey="valor" stroke={goldColors.main} strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      ) : (
                        <BarChart data={item.grafico.datos} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="etiqueta" angle={-30} textAnchor="end" interval={0} height={60} fontSize={12} />
                          <YAxis fontSize={12} />
                          <RechartsTooltip formatter={(value) => fmt(value)} />
                          <Bar dataKey="valor" fill={goldColors.main} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>
                )}
                {idx < historialPreguntas.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
export default Reports;
